import { BusinessUnit, BUSINESS_UNIT_LABELS } from '@/types/common';

export enum PurchaseOrderStatus {
  CREATED = 'CREATED',
  PENDING_REVIEW = 'PENDING_REVIEW',
  APPROVED = 'APPROVED',
  RECEIVING = 'RECEIVING',
  REJECTED = 'REJECTED',
  CLOSED = 'CLOSED',
}

// Mirrors backend CompanyEntity enum — the legal entity that owns the units.
// Chosen on the purchase order and inherited by every vehicle received on it.
export enum CompanyEntity {
  DPI = 'DPI',
  DEI = 'DEI',
}

export const COMPANY_ENTITY_LABEL: Record<CompanyEntity, string> = {
  [CompanyEntity.DPI]: 'DPI',
  [CompanyEntity.DEI]: 'DEI',
};

export const COMPANY_ENTITY_OPTIONS = Object.values(CompanyEntity).map(
  (value) => ({
    value,
    label: COMPANY_ENTITY_LABEL[value],
  })
);

export const getCompanyEntityLabel = (value?: string | null) =>
  value ? COMPANY_ENTITY_LABEL[value as CompanyEntity] || value : '-';

// Reused from the fleet schema so there's a single source of truth for
// business unit values/labels across pages.
export { BusinessUnit };
export const BUSINESS_UNIT_LABEL = BUSINESS_UNIT_LABELS;

export const PURCHASE_ORDER_STATUS_LABEL: Record<PurchaseOrderStatus, string> =
  {
    [PurchaseOrderStatus.CREATED]: 'Created',
    [PurchaseOrderStatus.PENDING_REVIEW]: 'Pending Review',
    [PurchaseOrderStatus.APPROVED]: 'Approved',
    [PurchaseOrderStatus.RECEIVING]: 'Receiving',
    [PurchaseOrderStatus.REJECTED]: 'Rejected',
    [PurchaseOrderStatus.CLOSED]: 'Closed',
  };

export const STATUS_BADGE: Record<PurchaseOrderStatus, string> = {
  [PurchaseOrderStatus.CREATED]:
    'bg-yellow-100 text-yellow-800 border border-yellow-300',
  [PurchaseOrderStatus.PENDING_REVIEW]:
    'bg-indigo-100 text-indigo-800 border border-indigo-300',
  [PurchaseOrderStatus.APPROVED]:
    'bg-green-100 text-green-800 border border-green-300',
  [PurchaseOrderStatus.RECEIVING]:
    'bg-blue-100 text-blue-800 border border-blue-300',
  [PurchaseOrderStatus.REJECTED]:
    'bg-red-100 text-red-800 border border-red-300',
  [PurchaseOrderStatus.CLOSED]:
    'bg-gray-100 text-gray-600 border border-gray-300',
};

// timestamps are ISO 8601 strings; integers are numbers
export interface PurchaseOrder {
  id: string;
  ID?: string; // some endpoints return uppercase ID (Drizzle PK casing)
  code: string;
  oemID: string;
  modelID: string;
  description: string;
  totalUnits: number;
  receivedUnits?: number; // present on list rows only (receiving progress)
  entity: CompanyEntity | null; // null on orders created before entity existed
  requestingBusinessUnit: BusinessUnit;
  notes: string | null;
  clientID: number | null;
  clientName: string | null;
  demandEvidenceFileUrl: string | null;
  status: PurchaseOrderStatus;
  rejectionReason: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface PurchaseReceiveItem {
  ID: string;
  purchaseOrderID: string;
  // The fleet unit this line created. Null on rows received before units were
  // created automatically and never matched by the backfill. When set, vin /
  // platNomor / imei below are read from that unit, so ops corrections show up
  // here instead of drifting.
  vehicleID: string | null;
  vin: string;
  platNomor: string | null;
  imei: string | null;
  receivedAt: string;
  hubID: number | null;
  hubName: string | null;
  hubCityID: number | null;
  hubCityName: string | null;
  createdAt: string;
  createdBy: string;
}

export interface PurchaseOrderStatusHistory {
  ID: string;
  purchaseOrderID: string;
  fromStatus: string; // '' on the initial CREATED row
  toStatus: string;
  metadata: Record<string, any> | null; // e.g. { rejectionReason } on REJECTED
  createdBy: string;
  createdAt: string;
}

// GET /:id returns the PO fields PLUS statusHistories.
// Received items are fetched separately via the paginated receive-items endpoint.
export interface PurchaseOrderDetail extends PurchaseOrder {
  statusHistories: PurchaseOrderStatusHistory[]; // newest first
  modelName: string; // the ordered model, so the page shows a name not a uuid
  modelContractType: 'RTO' | 'RENT'; // RTO makes contract duration mandatory on receive
}

export interface Paginated<T> {
  data: T[];
  pagination: { total: number; size: number; page: number; lastPage: number };
}

export interface CreatePurchaseOrderBody {
  code: string; // required, unique (server returns 400 on duplicate)
  oemID: string; // required, must be an existing OEM uuid
  modelID: string; // required, must be an existing model uuid
  description: string; // required
  totalUnits: number; // required, integer >= 1
  entity: CompanyEntity; // required
  requestingBusinessUnit: BusinessUnit; // required
  notes?: string; // optional
  clientID?: number; // optional
  clientName?: string; // optional
  demandEvidenceFileUrl?: string; // optional — URL of uploaded PDF/image
  createdBy: string; // required (current user name)
}

// Receiving a unit creates its vehicle row in the same transaction, so this
// carries everything that unit needs — not just the receipt facts.
export interface ReceiveItemInput {
  vin: string; // required
  platNomor: string; // required — vehicle.platNomor is NOT NULL
  imei?: string; // optional — omit when blank
  color: string; // required — vehicle.color is NOT NULL, not derivable from the PO
  receivedAt: string; // required, ISO datetime e.g. '2026-05-25T08:30:00Z'
  contractStartDate?: string; // optional 'YYYY-MM-DD' — defaults to receivedAt
  contractDuration?: number; // months; required when the PO's model is RTO
  hubID: number; // required, integer
  hubName: string; // required
  hubCityID: number; // required, integer
  hubCityName: string; // required
  stnkLink?: string; // optional plain URL (not an upload)
  stnkAnnualExpiryDate?: string; // optional 'YYYY-MM-DD'
  stnkFiveYearExpiryDate?: string; // optional 'YYYY-MM-DD'
  vehicleImageURL?: string; // optional plain URL (not an upload)
}

// The fields the backend can reject a row on — anything else it validates is a
// whole-batch problem, not a cell.
export type ReceiveItemErrorField =
  | 'vin'
  | 'platNomor'
  | 'imei'
  | 'contractDuration';

// One rejected row of a receive batch. The backend validates the whole batch
// before writing anything, so a failed submit returns every bad row at once.
export interface ReceiveItemRowError {
  index: number; // 0-based position in the submitted items array
  vin: string;
  field: ReceiveItemErrorField;
  message: string;
}

export interface ReceivePurchaseItemsBody {
  items: ReceiveItemInput[]; // required, min length 1
  createdBy: string; // required
}

export interface ApproveBody {
  updatedBy: string;
  updatedByEmail?: string; // acting user's email — checked against the approver allowlist
}
export interface CloseBody {
  updatedBy: string;
}
export interface RejectBody {
  rejectionReason: string; // reason non-empty
  updatedBy: string;
  updatedByEmail?: string; // acting user's email — checked against the approver allowlist
}

export interface GetReceiveItemsParams {
  page?: number; // default 1
  size?: number; // default 10
  sort?: 'vin' | 'receivedAt' | 'createdAt'; // default 'receivedAt'
  order?: 'asc' | 'desc'; // default 'desc'
  search?: string; // matches vin
}

export interface GetPurchaseOrdersParams {
  page?: number; // default 1
  size?: number; // default 10
  sort?: 'code' | 'status' | 'createdAt'; // default 'createdAt'
  order?: 'asc' | 'desc'; // default 'desc'
  search?: string; // matches `code` (ILIKE)
  statuses?: PurchaseOrderStatus[]; // serialized as CSV
  businessUnits?: BusinessUnit[]; // serialized as CSV
  entities?: CompanyEntity[]; // serialized as CSV
  oemID?: string;
  modelID?: string;
}
